import {
  AddOutlined,
  ArrowBackOutlined,
  CloseRounded,
  KeyboardArrowRightOutlined,
} from "@mui/icons-material";
import { Button, Divider, IconButton } from "@mui/material";
import React, { useCallback, useState } from "react";
import Flx from "../../layout/Flx";
import OpenInNewTabButton from "../../ui/buttons/OpenInNewTabButton";
import Txt from "../../typography/Txt";
import ConfirmationDialog from "../../ui/ConfirmationDialog";
import { grey } from "@mui/material/colors";
import AnimatedCheckmark from "../../feedback/AnimatedCheckmark/AnimatedCheckMark";
import BasicModal from "../../modals/BasicModal";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { cachedDataItemSet } from "../../../redux/actions/cachedDataActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";

const CreateLoanFromAppButton = ({
  data,
  text = "Create Loan from App",
  typeKeyName, // 'loanApplication', 'quickApp', 'creditAuthorization'
  confirmationDialogTitle = "Create Loan From Application",
  icon = <AddOutlined className="thin" />,
  successModalCloseText,
}) => {
  const [show, setShow] = useState(false);

  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="primary"
        startIcon={icon}
        onClick={() => setShow(true)}
      >
        {text}
      </Button>
      <CreateLoanFromLoanAppModal
        application={data}
        onClose={() => setShow(false)}
        show={show}
        successModalCloseText={successModalCloseText}
        confirmationDialogTitle={confirmationDialogTitle}
        typeKeyName={typeKeyName}
      />
    </>
  );
};

const CreateLoanFromLoanAppModal = ({
  show,
  application,
  typeKeyName,
  successModalCloseText,
  confirmationDialogTitle,
  onClose,
}) => {
  const { loading, createLoanFromSubmission } = useUnderwritingHook();
  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onCreateLoanClick = useCallback(() => {
    const appId = application?._id;

    // console.log("Create loan from application:", { appId });

    // // dispatch(loanPipelineClear());
    // // dispatch(cachedDataItemSet("loanApps", null));
    // dispatch(cachedDataItemSet(typeKeyName, null));
    // dispatch(loanPipelineClear());
    // setSuccess({
    //   loanNumber: 1001,
    //   loanName: "Test Loan",
    //   loanProductType: "30 Year Fixed",
    //   loanPurpose: typeKeyName,
    // });
    // return;
    createLoanFromSubmission({
      applicationId: appId,
      submissionType: typeKeyName, // 'loanApplication', 'quickApp', 'creditAuthorization'
      onSuccessFn: (d) => {
        setSuccess(d);
        // dispatch(cachedDataItemSet(typeKeyName, null));
        // dispatch(loanPipelineClear());
        // dispatch(cachedDataItemSet("loanApps", null));
        // navigate(`/loans`);
      },
    });
  }, [application?._id, dispatch, typeKeyName]);

  const handleOnClose = useCallback(
    (props) => {
      setSuccess(false);
      dispatch(cachedDataItemSet(typeKeyName, null));
      dispatch(loanPipelineClear());
      onClose();
    },
    [typeKeyName],
  );

  if (!show) {
    return null;
  }

  if (success) {
    return (
      <BasicModal
        maxWidth="sm"
        show={show}
        message="The loan has been created successfully."
        loading={loading}
        onConfirm={onCreateLoanClick}
        onCancel={handleOnClose}
      >
        <IconButton
          onClick={handleOnClose}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseRounded className="thin" />
        </IconButton>
        <Flx column g={2} center>
          <AnimatedCheckmark size={64} />

          <Flx column g={0.5} center>
            <Txt bold fontSize={16}>
              Loan Created Successfully
            </Txt>
            <Txt secondary>
              Your loan has been successfully added to the system
            </Txt>
          </Flx>
          <Flx
            fw
            g={1.5}
            column
            sx={{
              background: grey[50],
              p: 2,
              borderRadius: 1,
              ".txt": { fontSize: "11px" },
            }}
          >
            <Flx fw jb gap={4}>
              <Txt>Loan Number:</Txt>
              <Txt bold sx={{ textAlign: "right" }}>
                #{success?.loanNumber}
              </Txt>
            </Flx>
            <Divider />
            <Flx fw jb gap={4}>
              <Txt>Loan Name:</Txt>
              <Txt bold sx={{ textAlign: "right" }}>
                {success?.loanName || `Loan #${success?.loanNumber}`}
              </Txt>
            </Flx>
            <Divider />
            <Flx fw jb gap={4}>
              <Txt>Product Type:</Txt>
              <Txt bold sx={{ textAlign: "right" }}>
                {success?.loanProductType}
              </Txt>
            </Flx>
            <Divider />
            <Flx fw jb gap={4}>
              <Txt>Purpose:</Txt>
              <Txt bold sx={{ textAlign: "right" }}>
                {success?.loanPurpose}
              </Txt>
            </Flx>
          </Flx>
        </Flx>
        <Flx
          g={1}
          jb
          ac
          fw
          sx={{
            mt: 6,
            ".txt, .MuiButtonBase-root": {
              fontSize: "12px",
            },
          }}
        >
          <Button
            variant="text"
            onClick={handleOnClose}
            startIcon={<ArrowBackOutlined className="thin" />}
          >
            {successModalCloseText || "Back to Application"}
          </Button>
          <Flx ac jb g={2}>
            <OpenInNewTabButton linkIcon to={`/loan/${success?.loanNumber}`}>
              <Txt sx={{ fontWeight: 500 }}>Open in New Tab</Txt>
            </OpenInNewTabButton>
            <Button
              onClick={() => navigate(`/loan/${success?.loanNumber}`)}
              endIcon={<KeyboardArrowRightOutlined className="thin" />}
            >
              Go to Loan {success?.loanNumber}
            </Button>
          </Flx>
        </Flx>
      </BasicModal>
    );
  }
  return (
    <ConfirmationDialog
      open={show}
      title={confirmationDialogTitle}
      message="Are you sure you want to create a loan from this application?"
      cancelText="Cancel"
      confirmText="Yes, Create New Loan"
      loading={loading}
      onConfirm={onCreateLoanClick}
      onCancel={handleOnClose}
    />
  );
};

export default CreateLoanFromAppButton;
